import { useMemo } from 'react';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { buildPaginatedUrl, type Paginated } from '~/lib/pagination';

/**
 * Generic paginated list hook (ADR-0023).
 *
 * Wraps `useInfiniteQuery` and consumes the shared `Paginated<T>` envelope.
 * Returns a flattened `items` array plus the standard infinite-query controls
 * — callers don't reason about pages, only about items + "is there more".
 *
 * For endpoints that need extra filter params (e.g. `?type=person`), pass
 * them in `params` and they'll be appended to every page request.
 */
export interface UsePaginatedListOptions {
  pageSize?: number;
  params?: Record<string, string | undefined>;
  enabled?: boolean;
  staleTime?: number;
  /** Opt-in: while a new key loads, keep showing the previous key's data
   *  when this returns true for that previous key (`isPlaceholderData`). */
  keepPreviousWhen?: (previousKey: QueryKey) => boolean;
}

/**
 * `Extra` is whatever the endpoint puts on the envelope BESIDE the page —
 * FRONT-2202's gallery owner counts are the first of them. It surfaces as
 * `extra`, read from the FIRST page only, which is the whole point: a field
 * describing the list as a whole is sent on every page and is identical on
 * each, so reading a later page's copy would be the same value fetched twice
 * and a `useMemo` over all of them. Endpoints that send nothing extra leave
 * the default and `extra` is simply undefined.
 */
export function usePaginatedList<T, Extra = unknown>(
  queryKey: QueryKey,
  path: string,
  options: UsePaginatedListOptions = {},
) {
  const { pageSize = 50, params, enabled, staleTime, keepPreviousWhen } = options;

  const query = useInfiniteQuery<Paginated<T> & Partial<Extra>>({
    queryKey,
    placeholderData: keepPreviousWhen
      ? (previous, previousQuery) => (previousQuery && keepPreviousWhen(previousQuery.queryKey) ? previous : undefined)
      : undefined,
    // FRONT-2202 (/pr-panel finding, 2026-09-21): TanStack's own
    // `AbortSignal` is forwarded to the client, which has always accepted
    // one. Without it, a control that changes the request — the gallery
    // owner tabs and sort, which fire undebounced on every click — leaves
    // each superseded fetch running to completion, so a quick pass along
    // the strip has three list reads in flight for results nobody will
    // read. Nothing was ever mis-rendered (TanStack keys each query
    // separately, so a late response lands in its OWN cache entry), which
    // is why it went unnoticed; it is wasted work, not a wrong answer.
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<T> & Partial<Extra>>(
        buildPaginatedUrl(path, (pageParam as string | null) ?? null, pageSize, params),
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    enabled,
    staleTime,
  });

  const items = useMemo<T[]>(() => {
    if (!query.data) return [];
    const out: T[] = [];
    for (const page of query.data.pages) out.push(...page.items);
    return out;
  }, [query.data]);

  const total = query.data?.pages[0]?.total;
  const extra = query.data?.pages[0] as (Paginated<T> & Partial<Extra>) | undefined;

  return {
    items,
    total,
    extra,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    isRefetchError: query.isRefetchError,
    isPlaceholderData: query.isPlaceholderData,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error,
  };
}
